import React, { useEffect, useRef, useState } from "react";
import { Modal, Checkbox, Loader, Button, ActionIcon } from "@mantine/core";
import {
	IconSearch,
	IconEye,
	IconExternalLink,
	IconRefresh,
} from "@tabler/icons-react";
import { searchBoxQuery } from "../../actions/searchBoxActions";
import { contextItemIcon } from "./contextItemIcon";
import {
	contextItemKey,
	PREVIEWABLE_TYPES,
	truncateClientName,
} from "./contextItemUtils";
import HighlightedText from "./HighlightedText";
import RowSubtitle from "./RowSubtitle";
import ClearableTextInput from "./ClearableTextInput";
import pillButtonStyle from "./pillButtonStyle";
import BrowseTab from "./Browse/BrowseTab";

const RESULT_LIMIT = 20;
const DEBOUNCE_MS = 500;
// Both tabs' bodies share this fixed height so the modal never resizes when
// flipping between Search and Browse, or navigating within Browse.
const TAB_BODY_HEIGHT = 540;

const ALL_FILTER_TYPES = [
	{ key: "everything", label: "Everything" },
	{ key: "document", label: "Documents" },
	{ key: "matter", label: "Matters" },
	{ key: "email", label: "Emails" },
	{ key: "matterNote", label: "Notes" },
];

// Email and Notes have no equivalent ID search, so their placeholder stays plain.
const SEARCH_PLACEHOLDER_SUFFIX = {
	everything: " (including by Document ID)",
	document: " (including by ID)",
	matter: " (including by ID)",
};

const ResultRow = ({
	item,
	checked,
	disabled,
	onToggle,
	onPreview,
	onGoTo,
	highlightQuery,
}) => {
	const [hovered, setHovered] = useState(false);
	return (
		<div
			onClick={disabled ? undefined : onToggle}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				padding: "8px 4px",
				borderBottom: "1px solid var(--lw-light-grey, #eee)",
				borderRadius: 4,
				cursor: disabled ? "default" : "pointer",
				background: hovered && !disabled ? "#f2f2f2" : "transparent",
			}}
		>
			<Checkbox
				checked={checked}
				disabled={disabled}
				onChange={onToggle}
				onClick={(e) => e.stopPropagation()}
				style={{ flexShrink: 0 }}
			/>
			{contextItemIcon(item.itemType, item.title)}
			<div style={{ minWidth: 0, flex: 1 }}>
				<div
					style={{
						fontSize: 14,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					<HighlightedText text={item.title} query={highlightQuery} />
				</div>
				<div style={{ fontSize: 12, color: "#666" }}>
					<RowSubtitle
						text={
							item.itemType === "Matter"
								? truncateClientName(item.clientName)
								: item.matterName
						}
						item={item}
						highlightQuery={highlightQuery}
					/>
				</div>
			</div>
			{onGoTo ? (
				<ActionIcon
					variant="subtle"
					size="sm"
					title="Go to in Browse"
					onClick={(e) => {
						e.stopPropagation();
						onGoTo(item);
					}}
					style={{ flexShrink: 0 }}
				>
					<IconExternalLink size={16} />
				</ActionIcon>
			) : null}
			{onPreview && PREVIEWABLE_TYPES.has(item.itemType) ? (
				<ActionIcon
					variant="subtle"
					size="sm"
					title="Preview"
					onClick={(e) => {
						e.stopPropagation();
						onPreview(item);
					}}
					style={{ flexShrink: 0 }}
				>
					<IconEye size={16} />
				</ActionIcon>
			) : null}
		</div>
	);
};

const AiAddContextModal = ({
	opened,
	onClose,
	existingItems = [],
	onAdd,
	recentMatterIDs,
	onPreview,
	previewOpen,
	matterID,
}) => {
	// a matterID scopes this to a single-matter chat: only docs/emails/notes
	// within that matter are searchable, there's no "Matters" filter
	const FILTER_TYPES = matterID
		? ALL_FILTER_TYPES.filter((f) => f.key !== "matter")
		: ALL_FILTER_TYPES;

	// query/filterType/results/selected intentionally persist across close/reopen
	// so the user can resume their search; MatterAiChat remounts this component
	// (via a key on sessionId) when a chat loads or a new chat starts, which
	// resets this state naturally
	const searchInputRef = useRef(null);
	const [query, setQuery] = useState("");
	const [filterType, setFilterType] = useState("everything");
	const [results, setResults] = useState(null);
	// The query text that actually produced `results` — kept separate from
	// `query` (which updates on every keystroke) so match highlighting only
	// ever reflects the results currently on screen, not whatever's still
	// sitting in the debounce window.
	const [resultsQuery, setResultsQuery] = useState("");
	const [loading, setLoading] = useState(false);
	// bumped by the refresh button to force the search effect below to
	// re-run the current query without otherwise changing it
	const [searchRefreshNonce, setSearchRefreshNonce] = useState(0);
	const [selected, setSelected] = useState({});
	const [activeTab, setActiveTab] = useState(0);
	// mount Browse lazily (only once the user actually opens that tab) so
	// opening this modal doesn't eagerly fetch the matters list
	const [browseEverOpened, setBrowseEverOpened] = useState(false);
	// Mantine's Modal unmounts its content when closed, so anything Browse's
	// own child components held in local state would reset every time the
	// modal is closed and reopened. Lifting the drilled-into
	// matter/folder/filter/sort position up here — alongside query/results/
	// selected, which already live here for the same reason — makes it
	// survive close/reopen exactly like Search already does.
	const [browseState, setBrowseState] = useState({
		selectedMatter: null,
		filterText: "",
		mattersSort: "nameAsc",
		contentRoot: "docs",
		pathSegments: [],
		folderSort: "nameAsc",
	});
	const updateBrowseState = (patch) =>
		setBrowseState((prev) => ({ ...prev, ...patch }));

	// A single undo stack for every "step into Browse" action (switching to
	// the Browse tab, Go To, selecting a matter, drilling into a folder,
	// clicking a breadcrumb, ...). Each pushes a same-URL history entry —
	// nothing ever shows in the address bar — purely so the browser/mouse
	// Back button (Alt+Left, trackpad back-swipe, or an explicit in-modal
	// "back" click, which just calls history.back() too) has something of
	// ours to pop; popping it calls the matching undo instead of leaving
	// the page. Once the stack is empty, Back behaves normally again.
	const backStackRef = useRef([]);
	const pushBackStep = (undo) => {
		backStackRef.current.push(undo);
		window.history.pushState(
			{ browseBackTrap: true },
			"",
			window.location.href
		);
	};
	useEffect(() => {
		const handlePopState = (event) => {
			if (!event.state?.browseBackTrap) return;
			backStackRef.current.pop()?.();
		};
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	useEffect(() => {
		if (!query.trim()) {
			setResults(null);
			setResultsQuery("");
			setLoading(false);
			return;
		}
		setLoading(true);
		// switching filters can leave a slower, now-stale request in flight;
		// without this guard it can resolve after a faster newer request and
		// clobber its results
		let stale = false;
		const timeout = setTimeout(() => {
			searchBoxQuery({
				query,
				recentMatterIDs,
				limit: RESULT_LIMIT,
				filterType,
				matterID,
			})
				.then((res) => {
					if (stale) return;
					setResults(res.mysql);
					setResultsQuery(query);
					setLoading(false);
				})
				.catch(() => {
					if (!stale) setLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			stale = true;
			clearTimeout(timeout);
		};
	}, [query, recentMatterIDs, filterType, matterID, searchRefreshNonce]);

	const handleSearchRefresh = () => setSearchRefreshNonce((n) => n + 1);

	const existingKeys = new Set(
		existingItems.map((item) => contextItemKey(item))
	);

	const items = results
		? [
				...(results.matterResults || []).map((m) => ({
					itemType: "Matter",
					id: m.id,
					title: m.name,
					clientName: m.clientName,
					customMatterID: m.customMatterID,
				})),
				...(results.documentResults || []).map((d) => ({
					itemType: "Document",
					id: d.id,
					title: d.name,
					matterName: d.matterName,
					matterID: d.matterID,
					basepath: d.basepath,
				})),
				...(results.emailResults || []).map((e) => ({
					itemType: "Email",
					id: e.id,
					title: e.name,
					matterName: e.matterName,
					matterID: e.matterID,
					basepath: e.basepath,
				})),
				...(results.matterNoteResults || []).map((n) => ({
					itemType: "Matter Note",
					id: n.id,
					title: n.name,
					matterName: n.matterName,
					matterID: n.matterID,
					basepath: n.basepath,
				})),
			]
		: [];

	const activeFilterLabel =
		FILTER_TYPES.find((f) => f.key === filterType)?.label || "Everything";

	const toggleItem = (item) => {
		const key = contextItemKey(item);
		setSelected((prev) => {
			const next = { ...prev };
			if (next[key]) delete next[key];
			else next[key] = item;
			return next;
		});
	};

	const selectedItems = Object.values(selected);

	const handleAdd = () => {
		onAdd(selectedItems);
		// otherwise these items stay "selected" (and re-counted in the
		// button below) even once they're already added — existingItems
		// disables their checkboxes next time, but the footer shouldn't
		// keep counting them as a pending new selection
		setSelected({});
		onClose();
	};

	// "Go to": jump straight to a Search result's spot in Browse — the
	// matter's Documents root for a Matter result, or the containing folder
	// (in the matching Documents/Email/Notes tab) for everything else.
	// matterID/basepath for Document/Email/Matter Note results already come
	// straight off the searchBox response (see searchBoxManager.js) — no
	// extra request needed here.
	const handleGoTo = (item) => {
		const matterID = item.itemType === "Matter" ? item.id : item.matterID;
		const basepath = item.itemType === "Matter" ? "docs/" : item.basepath;
		if (!basepath) return;
		const [contentRoot, ...pathSegments] = basepath
			.split("/")
			.filter(Boolean);
		// Firm Docs documents share DB matterID 0 for both Public/Private —
		// only the basepath root tells them apart (see mapFirmDocsMatterID)
		const isFirmDocs =
			!matterID &&
			(contentRoot === "public" || contentRoot === "private");
		updateBrowseState({
			selectedMatter: {
				id: isFirmDocs ? (contentRoot === "public" ? -1 : 0) : matterID,
				name: isFirmDocs
					? contentRoot === "public"
						? "Public Firm Docs"
						: "Private Firm Docs"
					: item.itemType === "Matter"
						? item.title
						: item.matterName,
				clientName: item.clientName,
				isFirmDocs,
			},
			contentRoot: contentRoot || "docs",
			pathSegments,
			folderSort: "nameAsc",
		});
		setBrowseEverOpened(true);
		// Go To is one atomic jump — one Back press should return straight to
		// this Search result, not walk back out through the folder depth it
		// jumped into.
		pushBackStep(() => setActiveTab(0));
		setActiveTab(1);
	};

	// Shared by the header's Search/Browse pills and Go To above — pushing a
	// back-step is only needed going Search -> Browse (see backStackRef).
	const handleTabChange = (value) => {
		if (value === 1 && activeTab === 0) {
			pushBackStep(() => setActiveTab(0));
		}
		setActiveTab(value);
		if (value === 1) setBrowseEverOpened(true);
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<span>Add from LexWorkplace</span>
					<div style={{ display: "flex", gap: 8 }}>
						{["Search", "Browse"].map((label, i) => (
							<button
								key={label}
								type="button"
								onClick={() => handleTabChange(i)}
								style={pillButtonStyle(activeTab === i)}
							>
								{label}
							</button>
						))}
					</div>
				</div>
			}
			size="960px"
			// let the preview pane handle Escape when it's open on top of this modal
			closeOnEscape={!previewOpen}
			// plain autoFocus on the input isn't reliable here — Mantine's own
			// focus trap grabs focus back once the open transition finishes,
			// so focus it explicitly once that settles instead
			transitionProps={{
				onEntered: () => searchInputRef.current?.focus(),
			}}
		>
			<div
				style={{
					display: activeTab === 0 ? "flex" : "none",
					flexDirection: "column",
					height: TAB_BODY_HEIGHT,
					marginTop: 12,
				}}
			>
				<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
					<ClearableTextInput
						ref={searchInputRef}
						style={{ flex: 1 }}
						placeholder={`Search ${activeFilterLabel}${SEARCH_PLACEHOLDER_SUFFIX[filterType] ?? ""}...`}
						leftSection={<IconSearch size={16} />}
						value={query}
						onChange={(e) => setQuery(e.currentTarget.value)}
						onClear={() => setQuery("")}
					/>
					<ActionIcon
						variant="default"
						title="Refresh"
						onClick={handleSearchRefresh}
						style={{
							height: "var(--input-height-sm, 36px)",
							width: "var(--input-height-sm, 36px)",
							flexShrink: 0,
						}}
					>
						<IconRefresh size={16} />
					</ActionIcon>
				</div>
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						marginTop: 12,
					}}
				>
					{FILTER_TYPES.map(({ key, label }) => (
						<button
							key={key}
							type="button"
							onClick={() => setFilterType(key)}
							style={pillButtonStyle(filterType === key)}
						>
							{label}
						</button>
					))}
				</div>
				<div
					style={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						marginTop: 12,
					}}
				>
					{loading ? (
						<div style={{ textAlign: "center", padding: 24 }}>
							<Loader size={24} />
						</div>
					) : !query.trim() ? (
						<div
							style={{
								padding: 24,
								textAlign: "center",
								color: "#888",
							}}
						>
							Start typing to search {activeFilterLabel}.
						</div>
					) : items.length === 0 ? (
						<div
							style={{
								padding: 24,
								textAlign: "center",
								color: "#888",
							}}
						>
							No matches found.
						</div>
					) : (
						items.map((item) => {
							const key = contextItemKey(item);
							return (
								<ResultRow
									key={key}
									item={item}
									checked={
										!!selected[key] || existingKeys.has(key)
									}
									disabled={existingKeys.has(key)}
									onToggle={() => toggleItem(item)}
									onPreview={onPreview}
									onGoTo={handleGoTo}
									highlightQuery={resultsQuery}
								/>
							);
						})
					)}
				</div>
			</div>
			{browseEverOpened ? (
				<div
					style={{
						display: activeTab === 1 ? "flex" : "none",
						flexDirection: "column",
						height: TAB_BODY_HEIGHT,
						marginTop: 12,
					}}
				>
					<BrowseTab
						matterID={matterID}
						existingItems={existingItems}
						selected={selected}
						onToggle={toggleItem}
						onPreview={onPreview}
						browseState={browseState}
						onUpdateBrowseState={updateBrowseState}
						pushBackStep={pushBackStep}
					/>
				</div>
			) : null}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginTop: 16,
				}}
			>
				<span style={{ fontSize: 13, color: "#666" }}>
					{selectedItems.length} selected
				</span>
				<div style={{ display: "flex", gap: 8 }}>
					<Button variant="default" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={handleAdd}
						disabled={selectedItems.length === 0}
					>
						{selectedItems.length === 0
							? "Add to Context"
							: `Add to Context (${selectedItems.length})`}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default AiAddContextModal;
